/**
 * providerDetection.js
 *
 * Reliable MetaMask/Web3 provider detection utility.
 * Uses official @metamask/detect-provider package to avoid race conditions.
 */

import detectEthereumProvider from "@metamask/detect-provider";

let cachedProvider = null;
let cachedEthersProvider = null;
let cachedSigner = null;
let detectionPromise = null;

export async function detectProvider({
  mustBeMetaMask = false,
  silent = false,
  timeout = 3000,
} = {}) {
  if (cachedProvider && cachedProvider.isConnected?.()) {
    return cachedProvider;
  }

  if (detectionPromise) {
    return detectionPromise;
  }

  detectionPromise = (async () => {
    try {
      const provider = await detectEthereumProvider({ mustBeMetaMask, silent, timeout });
      if (!provider) return null;
      if (mustBeMetaMask && !provider.isMetaMask) return null;
      cachedProvider = provider;
      return provider;
    } catch {
      return null;
    } finally {
      detectionPromise = null;
    }
  })();

  return detectionPromise;
}

export async function getEthersProvider() {
  const ethereum = await detectProvider({ mustBeMetaMask: true });
  if (!ethereum) throw new Error("MetaMask not available");

  const { ethers } = await import("ethers");

  if (!cachedEthersProvider || cachedEthersProvider.provider !== ethereum) {
    cachedEthersProvider = new ethers.providers.Web3Provider(ethereum);
    cachedSigner = null;
  }

  return { ethersProvider: cachedEthersProvider, ethers };
}

export async function getSigner(expectedAccount = null) {
  const { ethersProvider } = await getEthersProvider();

  // Always get a fresh signer when account is specified (post-connect)
  if (expectedAccount) {
    const freshSigner = ethersProvider.getSigner();
    try {
      const signerAddress = await freshSigner.getAddress();
      if (signerAddress.toLowerCase() !== expectedAccount.toLowerCase()) {
        throw new Error(`Signer account mismatch: ${signerAddress} vs ${expectedAccount}`);
      }
      return freshSigner;
    } catch (error) {
      if (error.code === "UNSUPPORTED_OPERATION") {
        // ethers v5: provider not bound to account yet - clear cache and retry
        cachedEthersProvider = null;
        cachedSigner = null;
        // Re-get provider (MetaMask should still be connected)
        const { ethersProvider: refreshed } = await getEthersProvider();
        // Get fresh signer again (not cached)
        return refreshed.getSigner();
      }
      throw error;
    }
  }

  // No account specified - use cached signer if available
  if (!cachedSigner) {
    cachedSigner = ethersProvider.getSigner();
  }
  return cachedSigner;
}

export function clearProviderCache() {
  cachedProvider = null;
  cachedEthersProvider = null;
  cachedSigner = null;
  detectionPromise = null;
}
